
import { Alert, Card, Col, ConfigProvider, DatePicker, Divider, Form, InputNumber, List, Modal, Row, Select, Typography } from "antd";
import SDIModal from "../../../../components/ui/SDIModal";
import PropTypes from "prop-types";
import frFR from "antd/locale/fr_FR";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../../../variables/api/axiosInstance";
import BarreBottom from "../../../../components/docs/BarreBottom";
import dayjs from "dayjs";
import AvenantAgencySelector from "./AvenantAgencySelector";
import { applyAvenantAgenceCommission } from "../../../../services/avenantCommissionService";
import { rightsService } from "../../../../services/rightsService";

const { Text, Title } = Typography;

const AugmentationDeGarantie = (props) => {
    const [modalAugmentationGarantie, setModalAugmentationGarantie] = useState(
        props.open
    );

    const [loading, setLoading] = useState(false);
    const [garanties, setGaranties] = useState([[]]);
    const [compulsoryGuaranties, setCompulsoryGuaranties] = useState([]);
    const [currentPreview, setCurrentPreview] = useState("");
    const [preview, setPreview] = useState("");
    const [dateEffetAvenant, setDateEffetAvenant] = useState(() =>
        props?.contrat?.dateEffet ? dayjs(props?.contrat?.dateEffet) : dayjs()
    );
    // Agence ciblée + taux (siège) à appliquer au contrat résultant de l'avenant
    const [avCom, setAvCom] = useState({ agence: null, taux: null, intermediaire: null });

    // Édition EN PLACE d'une proposition AG existante (bouton « Modifier ») vs création d'un nouvel avenant.
    const isEditProposition = props?.contrat?.statutContrat === "EN_PROPOSITION"
        && props?.contrat?.avenant?.code === "AG";

    // Frais accessoires éditables sous habilitation HCO079 (comme au CV / renouvellement).
    const canEditAccessoires = rightsService("HCO079");
    const [accessoiresEdites, setAccessoiresEdites] = useState(null);

    useEffect(() => {
        if (props?.contrat?.codeId) {
            axiosInstance
                .get(
                    `/api/contrat/preview/${props?.contrat?.codeId}`
                )
                .then((response) => {
                    // console.log("AG Preview: ", response.data);
                    setCurrentPreview(response.data);
                })
                .catch((err) => {
                    console.log(err);
                });
        }
    }, [props?.contrat?.codeId]);

    const { CheckCircleOutlined, PlusCircleOutlined, SafetyCertificateOutlined } = require('@ant-design/icons');

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(value);
    };

    useEffect(() => {
        setModalAugmentationGarantie(props.open);
        if (props.open) {
            setDateEffetAvenant(props?.contrat?.dateEffet ? dayjs(props?.contrat?.dateEffet) : dayjs());
            // En édition d'une proposition AG, on demande la liste AVEC les garanties déjà ajoutées
            // (forAgEdit=true) → elles redeviennent sélectionnables et pré-remplissables.
            const url = `/api/garantie/non-compulsory/engins/${props?.contrat?.codeId}`
                + (isEditProposition ? `?forAgEdit=true` : ``);
            axiosInstance
                .get(url)
                .then((response) => {
                    const availableEngins = response.data || [];
                    setCompulsoryGuaranties(availableEngins);
                    // Pré-remplissage : mappe les garanties déjà ajoutées (recentlyAdded du preview) vers
                    // les options disponibles pour les pré-sélectionner.
                    if (isEditProposition) {
                        axiosInstance
                            .get(`/api/contrat/preview/${props?.contrat?.codeId}`)
                            .then((res) => prefillFromPreview(availableEngins, res.data))
                            .catch((e) => console.log(e));
                    }
                })
                .catch((err) => {
                    console.log(err);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open, props?.contrat?.dateEffet]);

    // Pré-remplit la sélection en mode ÉDITION : pour chaque engin, retrouve les options disponibles
    // dont le nom correspond à une garantie recentlyAdded du preview, pose l'état exactement comme la
    // sélection manuelle (garanties[i] = valeurs JSON, compulsoryGuaranties[i].sousGaranties = objets),
    // puis lance l'aperçu.
    const prefillFromPreview = (availableEngins, previewData) => {
        if (!previewData?.engins) return;
        const nextGaranties = [];
        const nextCompulsory = (availableEngins || []).map((engin, index) => {
            const previewEngin = previewData.engins.find((e) => e.codeId === engin.codeId)
                || previewData.engins[index];
            const addedNames = (previewEngin?.compulsoryGuaranties || [])
                .filter((g) => g.recentlyAdded)
                .map((g) => (g.nom || "").toLowerCase().trim());
            const matchingOptions = (engin.compulsoryGuaranties || [])
                .filter((g) => addedNames.includes((g.nom || "").toLowerCase().trim()));
            nextGaranties[index] = matchingOptions.map((g) => JSON.stringify(g));
            return { ...engin, sousGaranties: matchingOptions };
        });
        setGaranties(nextGaranties);
        setCompulsoryGuaranties(nextCompulsory);
        if (nextCompulsory.some((e) => (e.sousGaranties || []).length > 0)) {
            triggerPreview(nextCompulsory, props?.contrat?.dateEffet ? dayjs(props?.contrat?.dateEffet) : dayjs());
        }
    };

    const navigate = useNavigate();

    const handleSousGarantie = (index, value) => {
        var gtys = [];
        if (value.length === 1) {
            gtys.push(JSON.parse(value));
        } else {
            value.forEach((element) => {
                try {
                    gtys.push(JSON.parse(element));
                } catch (err) {
                    gtys.push(element);
                }
            });
        }
        let compulsoryGuarantiesCopy = [...compulsoryGuaranties];
        compulsoryGuarantiesCopy?.forEach((e, i) => {
            if (i === index) {
                e.sousGaranties = gtys;
            }
        });

        setCompulsoryGuaranties(compulsoryGuarantiesCopy);
        triggerPreview(compulsoryGuarantiesCopy, dateEffetAvenant);
    };

    const triggerPreview = (payloadSource, selectedDate) => {
        const formattedDate = selectedDate ? selectedDate.startOf('day').format('YYYY-MM-DDTHH:mm:ss') : null;
        const payload = Array.isArray(payloadSource)
            ? payloadSource.map((engin) => ({
                ...engin,
                dateEffetAvenant: formattedDate,
            }))
            : [];

        // Query string : dateEffet (DATE seule → prorata backend sur la date d'effet de l'avenant)
        // + accessoires édités (HCO079). Construits proprement pour éviter les ?/& en double.
        const dateParam = selectedDate ? selectedDate.format('YYYY-MM-DD') : null;
        const accEdit = (canEditAccessoires && accessoiresEdites != null && accessoiresEdites !== "");
        const qs = [
            dateParam ? `dateEffet=${dateParam}` : null,
            accEdit ? `accessoires=${Number(accessoiresEdites)}` : null,
        ].filter(Boolean).join("&");
        axiosInstance
            .put(
                `/api/engin/preview-augmentation-garantie/${props?.contrat?.codeId}${qs ? `?${qs}` : ""}`,
                payload
            )
            .then((ress) => {
                // console.log("PREVIEW: ", ress.data);
                setPreview(ress.data);
            })
            .catch((errr) => {
                console.log(errr);
            });
    };

    // Recalcule l'aperçu quand les frais accessoires édités changent (si une sélection existe).
    useEffect(() => {
        if (canEditAccessoires && Array.isArray(compulsoryGuaranties)
            && compulsoryGuaranties.some((e) => (e.sousGaranties || []).length > 0)) {
            triggerPreview(compulsoryGuaranties, dateEffetAvenant);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessoiresEdites]);

    const handleDateEffetChange = (date) => {
        setDateEffetAvenant(date);
        if (compulsoryGuaranties?.length) {
            triggerPreview(compulsoryGuaranties, date);
        }
    };


    const handleGarantiesChange = (index, e) => {
        let newGaranties = [...garanties];
        newGaranties[index] = e;
        setGaranties(newGaranties);
        handleSousGarantie(index, e);
    };

    const enterLoading = () => {
        setLoading(true);
    };

    const outLoading = () => {
        setLoading(false);
    };

    const handleConfirmAndSubmit = () => {
        const totalAmount = formatCurrency(preview?.primeTotale || 0);
        const newGuaranties = preview?.engins?.flatMap(e =>
            e.compulsoryGuaranties?.filter(g => g.recentlyAdded) || []
        ) || [];

        Modal.confirm({
            title: "Confirmer l'augmentation de garanties",
            content: (
                <div>
                    <p>Vous êtes sur le point d'appliquer cet avenant d'augmentation de garanties.</p>
                    {newGuaranties.length > 0 && (
                        <div style={{ margin: "8px 0" }}>
                            <strong>Nouvelles garanties :</strong>
                            <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                                {newGuaranties.map((g, i) => (
                                    <li key={i}>{g.nom} — {formatCurrency(g.capital)}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <p style={{ marginTop: "8px" }}>
                        <strong>Total à payer : {totalAmount}</strong>
                    </p>
                    <p>Cette action est irréversible. Confirmer ?</p>
                </div>
            ),
            okText: "Confirmer",
            cancelText: "Annuler",
            okType: "primary",
            onOk: handleAugmentationSubmit,
        });
    };

    const handleAugmentationSubmit = () => {
        enterLoading();
        console.log("All: ", preview);
        if (preview?.engins?.length > 0) {
            const formattedDate = dateEffetAvenant ? dateEffetAvenant.startOf('day').format('YYYY-MM-DDTHH:mm:ss') : null;
            const payload = {
                ...preview,
                dateEffetAvenant: formattedDate,
                ...(formattedDate ? { dateEffet: formattedDate } : {})
            };
            // Édition EN PLACE d'une proposition AG (bouton « Modifier ») vs création. Le backend
            // /proposition révoque les garanties précédemment ajoutées puis applique la nouvelle
            // sélection sur la MÊME version (pas d'empilement d'avenant). isEditProposition = const composant.
            const submitUrl = isEditProposition
                ? `/api/engin/augmentation-garantie/proposition`
                : `/api/engin/augmentation-garantie`;
            axiosInstance
                .put(
                    submitUrl,
                    JSON.stringify(payload))
                .then(async (resEngin) => {
                    await applyAvenantAgenceCommission(resEngin.data?.codeId, avCom.agence, avCom.taux, avCom.intermediaire);
                    toast("Augmentation de garanties effectuée!");
                    outLoading();
                    setModalAugmentationGarantie(false);
                    props.update(false);
                    navigate(`/details-contrat/${resEngin.data.codeId}`, { replace: true });
                    window.location.reload();
                })
                .catch((errEngin) => {
                    console.log(errEngin);
                    toast("Erreur lors de l'augmentation des garanties");
                });

        } else {
            toast("Aucune garantie ajoutée");
            outLoading();
            setModalAugmentationGarantie(false);
            props.update(false);
        }
    };

    const handleCancel = () => {
        setModalAugmentationGarantie(false);
        props.update(false);
    };

    return (
        <SDIModal
            title="AUGMENTATION DE GARANTIES"
            centered
            confirmLoading={loading}
            open={modalAugmentationGarantie}
            onOk={() => handleConfirmAndSubmit()}
            onCancel={() => handleCancel()}
            width={900}
            zIndex={1940}
        >
            <ConfigProvider locale={frFR}>
                <div className="augmentation-garanties-content">
                    <AvenantAgencySelector
                        organismeCode={props?.contrat?.organisme?.code}
                        defaultAgence={props?.contrat?.courtier || props?.contrat?.producteur?.agence || props?.contrat?.intermediaire?.agence}
                        defaultIntermediaire={props?.contrat?.intermediaire}
                        categorie={props?.contrat?.engins?.[0]?.categorie?.codeCategorie}
                        onChange={(ag, t, inter) => setAvCom({ agence: ag, taux: t, intermediaire: inter })}
                    />
                    <Card className="mb-3" size="small" title="Paramètres de l'avenant">
                        <Form layout="vertical">
                            <Form.Item label="Date d'effet de l'avenant" required>
                                <DatePicker
                                    value={dateEffetAvenant}
                                    onChange={handleDateEffetChange}
                                    format="DD/MM/YYYY"
                                    allowClear={false}
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                        </Form>
                    </Card>

                    {currentPreview?.engins?.map((item, index) => (
                        <div key={item.codeId} className="engin-section">
                            {/* Section Récapitulatif de l'engin */}
                            <Card className="mb-4" size="small" style={{ backgroundColor: "#14356a" }} >
                                <div className="engin-recap">
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <div className="info-item" >
                                                <Text style={{ color: "white" }} strong>Immatriculation:</Text>
                                                <Text style={{ color: "white" }} className="ml-2">{item.immatriculation}</Text>
                                            </div>
                                            <div className="info-item">
                                                <Text style={{ color: "white" }} strong>Marque:</Text>
                                                <Text style={{ color: "white" }} className="ml-2">{item.marque}</Text>
                                            </div>
                                        </Col>
                                        <Col span={12}>
                                            <div className="info-item">
                                                <Text style={{ color: "white" }} strong>N° Chassis:</Text>
                                                <Text style={{ color: "white" }} className="ml-2">{item.numeroChassis}</Text>
                                            </div>
                                            <div className="info-item">
                                                <Text style={{ color: "white" }} strong>Type:</Text>
                                                <Text style={{ color: "white" }} className="ml-2">{item.type || 'N/A'}</Text>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            </Card>

                            {/* Section Garanties actuelles */}
                            <Card className="mb-4" size="small" title="Garanties actuellement souscrites">
                                <Row gutter={16}>
                                    {/* Garanties obligatoires */}
                                    <Col span={12}>
                                        <div className="garanties-section">
                                            <Title level={5} className="section-title">
                                                <SafetyCertificateOutlined className="mr-2" />
                                                Garanties Obligatoires
                                            </Title>
                                            <List
                                                size="small"
                                                dataSource={item?.compulsoryGuaranties?.filter((garantie) => garantie.obligatoire && garantie.primeNette && !(isEditProposition && garantie.recentlyAdded)) || []}
                                                renderItem={(garantie) => (
                                                    <List.Item>
                                                        <div className="garantie-item">
                                                            <CheckCircleOutlined className="text-green-500 mr-2" />
                                                            <div>
                                                                <Text strong>{garantie.nom}</Text>
                                                                <div className="garantie-details">
                                                                    <Text type="secondary">
                                                                        Capital: {formatCurrency(garantie.capital)}
                                                                    </Text>
                                                                    <Text type="secondary" className="ml-3">
                                                                        Prime: {formatCurrency(garantie.primeNette)}
                                                                    </Text>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </List.Item>
                                                )}
                                            />
                                        </div>
                                    </Col>

                                    {/* Garanties optionnelles */}
                                    <Col span={12}>
                                        <div className="garanties-section">
                                            <Title level={5} className="section-title">
                                                <PlusCircleOutlined className="mr-2" />
                                                Garanties Optionnelles
                                            </Title>
                                            <List
                                                size="small"
                                                dataSource={item?.compulsoryGuaranties?.filter((garantie) => !garantie.obligatoire && !(isEditProposition && garantie.recentlyAdded)) || []}
                                                renderItem={(garantie) => (
                                                    <List.Item>
                                                        <div className="garantie-item">
                                                            <CheckCircleOutlined className="text-blue-500 mr-2" />
                                                            <div>
                                                                <Text strong>{garantie.nom}</Text>
                                                                <div className="garantie-details">
                                                                    <Text type="secondary">
                                                                        Capital: {formatCurrency(garantie.capital)}
                                                                    </Text>
                                                                    <Text type="secondary" className="ml-3">
                                                                        Prime: {formatCurrency(garantie.primeNette)}
                                                                    </Text>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </List.Item>
                                                )}
                                            />
                                        </div>
                                    </Col>
                                </Row>
                            </Card>

                            {/* Section Ajout de nouvelles garanties */}
                            <Card className="mb-4" size="small" title="Ajouter de nouvelles garanties">
                                {compulsoryGuaranties?.filter(e => e.codeId === item.codeId).length && compulsoryGuaranties?.find(e => e.codeId === item.codeId)?.compulsoryGuaranties?.length ? (
                                    <div>
                                        <Form layout="vertical">
                                            <Form.Item label="Sélectionner les garanties à ajouter">
                                                <Select
                                                    mode="multiple"
                                                    placeholder="Choisir les garanties..."
                                                    value={Array.isArray(garanties[index]) ? garanties[index] : []}
                                                    onChange={(event) => {
                                                        handleGarantiesChange(index, event);
                                                        handleSousGarantie(index, event);
                                                    }}
                                                    options={compulsoryGuaranties?.find(e => e.codeId === item.codeId)?.compulsoryGuaranties.map((guaranty, idx) => ({
                                                        label: (
                                                            <div className="garantie-option">
                                                                <div className="garantie-name">{guaranty.nom}</div>
                                                                <div className="garantie-price">
                                                                    <Text type="secondary">
                                                                        Prime Annuelle: {formatCurrency(guaranty.primeNette)} |
                                                                        Capital: {formatCurrency(guaranty.capital)}
                                                                    </Text>
                                                                </div>
                                                            </div>
                                                        ),
                                                        value: JSON.stringify(guaranty),
                                                        key: idx
                                                    }))}
                                                    style={{ width: "100%" }}
                                                />
                                            </Form.Item>
                                        </Form>
                                    </div>
                                ) : (
                                    <Alert
                                        message="Aucune garantie supplémentaire disponible"
                                        description="Toutes les garanties optionnelles sont déjà ajoutées à cet engin"
                                        type="warning"
                                        showIcon
                                    />
                                )}

                            </Card>

                            {/* Séparateur entre engins si plusieurs */}
                            {index < compulsoryGuaranties.length - 1 && <BarreBottom />}
                        </div>
                    ))}

                    {/* Récapitulatif des coûts */}
                    {garanties?.length > 0 && (
                        <Card className="cost-summary mt-3" size="small">
                            <Title level={5}>Récapitulatif des coûts</Title>
                            <div className="cost-breakdown">
                                <Row justify="space-between" className="cost-line">
                                    <Col>
                                        <Text>Date d'effet de l'avenant :</Text>
                                    </Col>
                                    <Col>
                                        <Text strong>{dateEffetAvenant ? dateEffetAvenant.format('DD/MM/YYYY') : '-'}</Text>
                                    </Col>
                                </Row>

                                <Divider className="my-2" />

                                <Row justify="space-between" className="cost-line">
                                    <Col>
                                        <Text>Prime nette au prorata:</Text>
                                    </Col>
                                    <Col>
                                        <Text strong>{formatCurrency(preview?.primeNette || 0)}</Text>
                                    </Col>
                                </Row>

                                <Row justify="space-between" align="top" className="cost-line">
                                    <Col>
                                        <Text>Accessoires:</Text>
                                    </Col>
                                    <Col style={{ textAlign: "right" }}>
                                        {canEditAccessoires ? (
                                            <>
                                                <InputNumber
                                                    size="small"
                                                    min={0}
                                                    style={{ width: 150 }}
                                                    value={accessoiresEdites != null ? accessoiresEdites : preview?.accessoires}
                                                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                                                    parser={(v) => v.replace(/\s/g, "")}
                                                    onChange={(v) => setAccessoiresEdites(v)}
                                                />
                                                <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                                                    Par défaut : {formatCurrency(preview?.accessoiresDefaut != null ? preview.accessoiresDefaut : preview?.accessoires)}
                                                </div>
                                            </>
                                        ) : (
                                            <Text>{formatCurrency(preview?.accessoires || 0)}</Text>
                                        )}
                                    </Col>
                                </Row>

                                <Row justify="space-between" className="cost-line">
                                    <Col>
                                        <Text>Taxes(6%) :</Text>
                                    </Col>
                                    <Col>
                                        <Text>{formatCurrency(preview?.taxes || 0)}</Text>
                                    </Col>
                                </Row>

                                <Divider className="my-2" />

                                <Row justify="space-between" className="cost-line total-cost">
                                    <Col>
                                        <Text strong className="text-lg">TOTAL À PAYER:</Text>
                                    </Col>
                                    <Col>
                                        <Text strong className="text-lg text-blue-600">
                                            {formatCurrency(preview?.primeTotale || 0)}
                                        </Text>
                                    </Col>
                                </Row>
                            </div>
                        </Card>
                    )}
                </div>
            </ConfigProvider>

            <style jsx>
                {`
                    .augmentation-garanties-content {
                        max-height: 70vh;
                        overflow-y: auto;
                    }
                    
                    .engin-section {
                        margin-bottom: 20px;
                    }
                    
                    .engin-recap .info-item {
                        margin-bottom: 8px;
                    }
                    
                    .garanties-section {
                        height: 100%;
                    }
                    
                    .section-title {
                        margin-bottom: 16px !important;
                        color: #1890ff;
                    }
                    
                    .garantie-item {
                        display: flex;
                        align-items: flex-start;
                        width: 100%;
                    }
                    
                    .garantie-details {
                        font-size: 12px;
                        margin-top: 4px;
                    }
                    
                    .garantie-option {
                        padding: 4px 0;
                    }
        
                    .garantie-name {
                        font-weight: 500;
                    }
                    
                    .garantie-price {
                        font-size: 12px;
                        margin-top: 2px;
                    }
                    
                    .cost-summary {
                        background-color: #f8f9fa;
                        border: 1px solid #e9ecef;
                    }
                    
                    .cost-breakdown .cost-line {
                        margin-bottom: 8px;
                    }
                    
                    .cost-breakdown .total-cost {
                        background-color: #e6f7ff;
                        padding: 8px;
                        border-radius: 4px;
                        margin-top: 8px;
                    }
                    
                    .ml-2 { margin-left: 8px; }
                    .ml-3 { margin-left: 12px; }
                    .mr-2 { margin-right: 8px; }
                    .mt-3 { margin-top: 12px; }
                    .mb-4 { margin-bottom: 16px; }
                    .my-2 { margin: 8px 0; }
                    .text-lg { font-size: 16px; }
                    .text-blue-600 { color: #1890ff; }
                    .text-green-500 { color: #52c41a; }
                    .text-blue-500 { color: #1890ff; }
                `}
            </style>
        </SDIModal>
    );
};

AugmentationDeGarantie.propTypes = {
    open: PropTypes.bool,
    update: PropTypes.func,
    contrat: PropTypes.object
};

export default AugmentationDeGarantie;
